import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useSettingsStore } from '@/store/settingsStore';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LanguageSelector } from '@/components/ui/language-selector';
import { OrganizationSelector } from './OrganizationSelector';
import {
  useOrganizations,
  useContextSwitcher,
  useSelectedOrganizationDetails,
} from '@/features/organizations/hooks';
import { User, Settings, LogOut, FolderOpen, Sun, Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/lib/constants/routes';
import { createLogoutFlow, performLogout } from '@/lib/api/kratos';

/**
 * UserMenu Component
 *
 * User dropdown menu with organization selector, organization settings, and logout.
 * Based on the original Nuxt dashboard user menu structure.
 */

// Picks the best human-readable label for the user header. Kratos
// `traits.name` is the canonical source but SSO-provisioned identities
// often arrive without it (Polis/IdP didn't emit a name claim). Falls
// back to a capitalized email-username so the menu never shows the
// literal placeholder "User".
function getDisplayName(user: { traits?: { name?: string; email?: string } }): string {
  const name = user.traits?.name?.trim();
  if (name) return name;
  const email = user.traits?.email;
  if (!email) return 'User';
  const localPart = email.split('@')[0] || '';
  if (!localPart) return 'User';
  return localPart.charAt(0).toUpperCase() + localPart.slice(1);
}

export function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const user = useAuthStore((state) => state.user);
  const clearSession = useAuthStore((state) => state.clearSession);
  // Use the enriched version so the dropdown shows the BFF-fetched
  // name (the store value carries the empty string written by the SSO
  // orchestrator / SCIM provisioner).
  const selectedOrganization = useSelectedOrganizationDetails();
  const theme = useSettingsStore((state) => state.theme);
  const setTheme = useSettingsStore((state) => state.setTheme);
  
  const { organizations, isLoading: isLoadingOrgs } = useOrganizations();
  const { switchOrganization } = useContextSwitcher();

  const handleLogout = async () => {
    try {
      setIsOpen(false);
      
      // Create logout flow and perform logout with Kratos
      const logoutFlow = await createLogoutFlow();
      await performLogout(logoutFlow.logout_token);
      
      // Clear local session state and all context
      clearSession();
      useSettingsStore.getState().clearAllContext();
      
      // Redirect to login page
      navigate(ROUTES.AUTH.LOGIN, { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      
      // Even if Kratos logout fails, clear local state and redirect
      clearSession();
      useSettingsStore.getState().clearAllContext();
      navigate(ROUTES.AUTH.LOGIN, { replace: true });
    }
  };

  const handleOrganizationSettings = () => {
    navigate(ROUTES.ORGANIZATION_SETTINGS.INDEX);
    setIsOpen(false);
  };

  const handleProjects = () => {
    navigate(ROUTES.PROJECTS);
    setIsOpen(false);
  };

  const handleThemeToggle = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  if (!user) return null;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0">
          <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center">
            <User className="h-4 w-4" />
          </div>
          <span className="sr-only">{t('common.profile')}</span>
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-72 sm:w-80 p-2 mr-2 sm:mr-0">
        {/* User Info */}
        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
            <User className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{getDisplayName(user)}</p>
            <p className="text-sm text-muted-foreground truncate">
              {user.traits.email}
            </p>
          </div>
        </div>

        <DropdownMenuSeparator />

        {/* Organization Selector. The section header is suppressed when
            the user only belongs to one org — there is nothing to
            select, so "Seleccionar organización" would be misleading. */}
        {organizations.length > 0 && (
          <>
            <div className="p-3">
              {organizations.length > 1 && (
                <div className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
                  {t('selectors.selectOrganization')}
                </div>
              )}
              <OrganizationSelector
                organizations={organizations}
                selectedOrganization={selectedOrganization}
                onOrganizationChange={switchOrganization}
                loading={isLoadingOrgs}
              />
            </div>
            <DropdownMenuSeparator className="my-2" />
          </>
        )}

        {/* Navigation Items */}
        <DropdownMenuItem onClick={handleProjects}>
          <FolderOpen className="mr-2 h-4 w-4" />
          {t('navigation.projects')}
        </DropdownMenuItem>

        <DropdownMenuItem onClick={handleOrganizationSettings}>
          <Settings className="mr-2 h-4 w-4" />
          {t('navigation.organizationSettings')}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Language Selector */}
        <div className="flex items-center justify-between px-2 py-1.5">
          <span className="text-sm">{t('common.language')}</span>
          <LanguageSelector />
        </div>

        <DropdownMenuSeparator />

        {/* Theme Toggle */}
        <DropdownMenuItem onClick={handleThemeToggle}>
          {theme === 'light' ? (
            <Moon className="mr-2 h-4 w-4" />
          ) : (
            <Sun className="mr-2 h-4 w-4" />
          )}
          {theme === 'light' ? t('common.darkMode') : t('common.lightMode')}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Logout */}
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          {t('common.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}